import React, { useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'

import { useTheme } from '../../hooks/useTheme'
import CampingFloorPlan from '../spaces/CampingFloorPlan'
import type { EspacioDisponible } from '../../types'

interface QRLocatorData {
  valid: boolean
  message: string
  reserva_id?: string
  cliente_nombre?: string
  espacio_nombre?: string
  espacio_id?: string
  tipo_coordenadas?: 'plano' | 'ubicacion' | 'poligono_mapa' | 'plano_vertices'
  ubicacion?: {
    lat: number
    lng: number
  }
  fecha_entrada?: string
  fecha_salida?: string
  plano_x?: number
  plano_y?: number
  plano_radio?: number
  plano_vertices?: Array<{ x: number; y: number }>
  poligono_wkt?: string
  ubicacion_wkt?: string
  indicaciones?: any  // Polígono/rectángulo/círculo del TipoEspacios
  instrucciones?: string  // Texto con instrucciones
}

interface QRLocatorResultProps {
  data: QRLocatorData | null
  isLoading: boolean
  onClose: () => void
}

const QRLocatorResult: React.FC<QRLocatorResultProps> = ({
  data,
  isLoading,
  onClose,
}) => {
  const { colors } = useTheme()
  const planoImageUrl = `${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`

  // No mostrar toast aquí - el modal es suficiente

  if (!data) return null

  // Modal de error
  if (!data.valid) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header con color según estado */}
          <div
            className="px-6 py-8 text-center"
            style={{
              backgroundColor: '#FEE2E2', // Rojo suave
            }}
          >
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ubicación No Disponible
            </h2>
            <p className="text-sm font-medium" style={{ color: '#DC2626' }}>
              {data.message}
            </p>
          </div>

          {/* Contenido - Sugerencia */}
          <div className="px-6 py-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-900 font-semibold mb-2">
                💡 Si tienes problemas con la cámara:
              </p>
              <p className="text-xs text-blue-800">
                Puedes ingresar manualmente el código QR en el campo de texto disponible en esta página.
              </p>
            </div>
          </div>

          {/* Botón cerrar */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-3 rounded-lg font-medium text-white transition-colors"
              style={{
                backgroundColor: colors.primary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Validar que tenga coordenadas de cualquier tipo (plano, ubicacion, polygon, etc)
  const tieneCoordenadas =
    (data.plano_x !== undefined && data.plano_y !== undefined) ||  // Plano 2D
    (data.ubicacion?.lat !== undefined && data.ubicacion?.lng !== undefined) ||  // Geográficas
    (data.plano_vertices !== undefined && data.plano_vertices.length >= 3) ||  // Polígono
    (data.poligono_wkt !== undefined) ||  // PostGIS polygon
    (data.ubicacion_wkt !== undefined)  // WKT geography

  // Modal de éxito sin coordenadas - mostrar como error
  if (!tieneCoordenadas) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header con color según estado */}
          <div
            className="px-6 py-8 text-center"
            style={{
              backgroundColor: '#FEE2E2', // Rojo suave
            }}
          >
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ubicación No Disponible
            </h2>
            <p className="text-sm font-medium" style={{ color: '#DC2626' }}>
              El espacio no tiene coordenadas en el plano
            </p>
          </div>

          {/* Contenido - Sugerencia */}
          <div className="px-6 py-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-900 font-semibold mb-2">
                ℹ️ Información:
              </p>
              <p className="text-xs text-blue-800">
                La reserva existe pero el espacio aún no tiene coordenadas asignadas en el plano del camping.
              </p>
            </div>
          </div>

          {/* Botón cerrar */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-3 rounded-lg font-medium text-white transition-colors"
              style={{
                backgroundColor: colors.primary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Convertir locatorResult a formato EspacioDisponible para mostrar en el plano (solo si tiene plano_x/plano_y)
  const espacioDelQR: EspacioDisponible[] = data && data.plano_x !== undefined && data.plano_y !== undefined
    ? [{
        id: data.espacio_id || '',
        codigo: `${data.espacio_nombre}`,
        nombre: data.espacio_nombre || '',
        descripcion: '',
        tipo_nombre: '',
        tarifa_noche: 0,
        capacidad_maxima: 0,
        comodidades: [],
        imagenes: [],
        disponible: true,
        total_dias_disponibles: 0,
        total_dias_rango: 0,
        plano_x: data.plano_x,
        plano_y: data.plano_y,
        plano_radio: data.plano_radio || 30,
        reservable: false
      }]
    : []

  // Mensajes personalizados según tipo de coordenadas
  const getTipoCoordenadasText = (): string => {
    switch (data.tipo_coordenadas) {
      case 'plano':
        return 'Ubicación en el plano del camping'
      case 'ubicacion':
        return 'Ubicación geográfica (coordenadas GPS)'
      case 'poligono_mapa':
        return 'Ubicación con área poligonal'
      case 'plano_vertices':
        return 'Ubicación con polígono en el plano'
      default:
        return 'Ubicación en el plano'
    }
  }

  // Modal de éxito con coordenadas
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header con color según estado */}
        <div
          className="px-6 py-8 text-center sticky top-0 z-10"
          style={{
            backgroundColor: '#D1FAE5', // Verde suave
          }}
        >
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Ubicación Obtenida!
          </h2>
          <p className="text-sm font-medium" style={{ color: '#059669' }}>
            {data.message}
          </p>
        </div>

        {/* Contenido */}
        <div className="px-6 py-6 bg-gray-50">
          {/* Detalles rápidos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Cliente */}
            {data.cliente_nombre && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                  Cliente
                </p>
                <p className="text-sm font-bold text-gray-900 truncate">
                  {data.cliente_nombre}
                </p>
              </div>
            )}

            {/* Espacio */}
            {data.espacio_nombre && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                  Espacio
                </p>
                <p className="text-sm font-bold truncate" style={{ color: colors.primary }}>
                  {data.espacio_nombre}
                </p>
              </div>
            )}

            {/* Entrada */}
            {data.fecha_entrada && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                  Entrada
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {new Date(data.fecha_entrada).toLocaleDateString('es-CL')}
                </p>
              </div>
            )}

            {/* Salida */}
            {data.fecha_salida && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">
                  Salida
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {new Date(data.fecha_salida).toLocaleDateString('es-CL')}
                </p>
              </div>
            )}
          </div>

          {/* Instrucciones de texto del tipo de espacio */}
          {data.instrucciones && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📋</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Instrucciones
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {data.instrucciones}
                  </p>
                </div>
              </div>
            </div>
          )}

{/* Plano del camping - mostrar si tiene CUALQUIER tipo de coordenadas */}
          {(espacioDelQR.length > 0 || tieneCoordenadas) && (
            <div className="bg-white rounded-lg p-4 mb-4 relative">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                🗺️ {getTipoCoordenadasText()}
              </h3>

              {/* Indicador animado con flecha y dedo */}
              <style>{`
                @keyframes bounce {
                  0%, 100% { transform: translateY(0) scale(1); }
                  50% { transform: translateY(-8px) scale(1.1); }
                }
                @keyframes bounce-text {
                  0%, 100% { transform: translateY(0) translateX(0); }
                  50% { transform: translateY(-12px) translateX(2px); }
                }
                @keyframes pulse-glow {
                  0%, 100% { transform: scale(1); opacity: 0.8; }
                  50% { transform: scale(1.15); opacity: 0.4; }
                }
                .bounce-animation {
                  animation: bounce 1.2s infinite;
                }
                .bounce-text-animation {
                  animation: bounce-text 1.2s infinite;
                }
                .pulse-glow-animation {
                  animation: pulse-glow 2s infinite;
                }
              `}</style>

              <div className="relative w-full">
                {espacioDelQR.length > 0 ? (
                  <CampingFloorPlan
                    espacios={espacioDelQR}
                    planoImageUrl={planoImageUrl}
                    onReserve={() => {}}
                    searchQuery={null}
                    showAvailabilityIndicators={true}
                  />
                ) : (
                  // SVG directo sin CampingFloorPlan (para casos sin plano_x/y)
                  <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: '1920/1080' }}>
                    <img
                      src={planoImageUrl}
                      alt="Plano del camping"
                      className="w-full h-full object-cover"
                    />
                    {/* SVG para dibujar indicaciones y polígono */}
                    <svg
                      className="absolute inset-0 w-full h-full"
                      viewBox="0 0 1920 1080"
                      preserveAspectRatio="xMidYMid slice"
                    >

                      {/* Dibujar lugar exacto (plano_vertices del espacio) */}
                      {data.plano_vertices && data.plano_vertices.length >= 3 && (
                        <polygon
                          points={data.plano_vertices
                            .map((v: { x: number; y: number }) => `${v.x},${v.y}`)
                            .join(' ')}
                          fill="rgba(34, 197, 94, 0.2)"
                          stroke="#22c55e"
                          strokeWidth="3"
                          style={{
                            filter: 'drop-shadow(0 0 8px rgba(34, 197, 94, 0.6))',
                          }}
                        />
                      )}

                      {/* Objetivo/Crosshair animado tipo sniper scope */}
                      {data.plano_vertices && data.plano_vertices.length >= 3 && (() => {
                        // Centroide del espacio exacto
                        const sumX = data.plano_vertices.reduce((acc: number, v: { x: number; y: number }) => acc + v.x, 0);
                        const sumY = data.plano_vertices.reduce((acc: number, v: { x: number; y: number }) => acc + v.y, 0);
                        const cx = sumX / data.plano_vertices.length;
                        const cy = sumY / data.plano_vertices.length;

                        return (
                          <g>
                            {/* Anillos concéntricos que se cierran */}
                            <style>{`
                              @keyframes zoomIn {
                                0% { r: 200px; opacity: 0; }
                                50% { opacity: 1; }
                                100% { r: 60px; opacity: 0; }
                              }
                              @keyframes zoomIn2 {
                                0% { r: 150px; opacity: 0; }
                                50% { opacity: 1; }
                                100% { r: 40px; opacity: 0; }
                              }
                              .crosshair-ring1 { animation: zoomIn 2.5s infinite; }
                              .crosshair-ring2 { animation: zoomIn2 2.5s infinite 0.5s; }
                            `}</style>

                            {/* Anillos animados */}
                            <circle cx={cx} cy={cy} r="200" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring1" />
                            <circle cx={cx} cy={cy} r="150" fill="none" stroke="#ff0000" strokeWidth="2" className="crosshair-ring2" />

                            {/* Círculo central pequeño */}
                            <circle cx={cx} cy={cy} r="15" fill="none" stroke="#ff0000" strokeWidth="2" />

                            {/* Línea horizontal */}
                            <line x1={cx - 60} y1={cy} x2={cx - 30} y2={cy} stroke="#ff0000" strokeWidth="2" />
                            <line x1={cx + 30} y1={cy} x2={cx + 60} y2={cy} stroke="#ff0000" strokeWidth="2" />

                            {/* Línea vertical */}
                            <line x1={cx} y1={cy - 60} x2={cx} y2={cy - 30} stroke="#ff0000" strokeWidth="2" />
                            <line x1={cx} y1={cy + 30} x2={cx} y2={cy + 60} stroke="#ff0000" strokeWidth="2" />

                            {/* Punto central */}
                            <circle cx={cx} cy={cy} r="3" fill="#ff0000" />
                          </g>
                        );
                      })()}

                    </svg>

                  </div>
                )}

                {/* Indicador visual en las coordenadas del espacio */}
                {data.plano_x && data.plano_y && (
                  <>
                    {/* Dedo indicador que apunta al espacio */}
                    <div
                      className="absolute z-30 pointer-events-none"
                      style={{
                        left: `calc(${(data.plano_x / 1920) * 100}% - 35px)`,
                        top: `calc(${(data.plano_y / 1080) * 100}% - 90px)`,
                      }}
                    >
                      <div className="bounce-animation text-5xl">👇</div>
                    </div>

                    {/* Polígono/forma de las indicaciones del tipo (si existe) */}
                    {data.indicaciones && (
                      <svg
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox="0 0 1920 1080"
                        preserveAspectRatio="xMidYMid slice"
                      >
                        {data.indicaciones.tipo === 'rectangle' && (
                          <rect
                            x={data.indicaciones.x1}
                            y={data.indicaciones.y1}
                            width={data.indicaciones.x2 - data.indicaciones.x1}
                            height={data.indicaciones.y2 - data.indicaciones.y1}
                            fill="rgba(59, 130, 246, 0.15)"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            style={{
                              filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))',
                            }}
                          />
                        )}
                        {data.indicaciones.tipo === 'circle' && (
                          <circle
                            cx={data.indicaciones.x}
                            cy={data.indicaciones.y}
                            r={data.indicaciones.radio}
                            fill="rgba(59, 130, 246, 0.15)"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            style={{
                              filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))',
                            }}
                          />
                        )}
                        {data.indicaciones.tipo === 'polygon' && data.indicaciones.vertices && (
                          <polygon
                            points={data.indicaciones.vertices
                              .map((v: { x: number; y: number }) => `${v.x},${v.y}`)
                              .join(' ')}
                            fill="rgba(59, 130, 246, 0.15)"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            style={{
                              filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))',
                            }}
                          />
                        )}
                      </svg>
                    )}

                    {/* Círculos pulsadores alrededor del espacio */}
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        opacity: 0.9,
                      }}
                      viewBox="0 0 1920 1080"
                      preserveAspectRatio="xMidYMid slice"
                    >
                      {/* Círculo pulsador grande */}
                      <circle
                        cx={data.plano_x}
                        cy={data.plano_y}
                        r={(data.plano_radio || 30) + 40}
                        fill="none"
                        stroke="#ef4444"
                        strokeWidth="4"
                        className="pulse-glow-animation"
                        style={{
                          filter: 'drop-shadow(0 0 12px rgba(239, 68, 68, 0.8))',
                        }}
                      />
                      {/* Círculo interior punteado */}
                      <circle
                        cx={data.plano_x}
                        cy={data.plano_y}
                        r={(data.plano_radio || 30) + 25}
                        fill="none"
                        stroke="#f87171"
                        strokeWidth="2"
                        strokeDasharray="6,4"
                        style={{
                          opacity: 0.7,
                        }}
                      />
                    </svg>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Botón cerrar */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 sticky bottom-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 rounded-lg font-medium text-white transition-colors"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default QRLocatorResult
